import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { Box } from "@mui/material";
import Paper from "@mui/material/Paper";
import useDeviceDetect from "../Detect/useDeviceDetect ";
import ItemLedgerCard from "./ItemLedgerCard";
import ItemLedgerTable from "./ItemLedgerTable";
import ItemLedgerFilter from "./ItemLedgerFilter";
import PopupDialogTitle from "../Dialog/PopupDialogTitle";
import Calculation from "../../utility/Calculations";
import { triggerLoader } from "../../redux/reducers/LoaderTrigger";
import { useDispatch } from "react-redux";
import { filterItemLedgerRows } from "./itemLedgerFilterUtils";

const resetFilterState = (setters) => {
  setters.setFilterField("");
  setters.setFilterValue("");
  setters.setDateFrom(null);
  setters.setDateTo(null);
  setters.setAppliedFilters({});
};

//FUNCTION
const ItemLedgerPopup = memo(
  ({
    openItemLedger,
    handleItemLedgerClose,
    itemLedger,
    selectedItem,
    itemLedgerLoading,
  }) => {
    const { isMobile } = useDeviceDetect();
    const dispatch = useDispatch();
    const itemRef = useRef();

    const [filterField, setFilterField] = useState("");
    const [filterValue, setFilterValue] = useState("");
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [appliedFilters, setAppliedFilters] = useState({});

    const filteredItemLedger = useMemo(
      () => filterItemLedgerRows(itemLedger, appliedFilters),
      [itemLedger, appliedFilters]
    );

    useEffect(() => {
      if (!openItemLedger) {
        resetFilterState({
          setFilterField,
          setFilterValue,
          setDateFrom,
          setDateTo,
          setAppliedFilters,
        });
      }
    }, [openItemLedger]);

    useEffect(() => {
      resetFilterState({
        setFilterField,
        setFilterValue,
        setDateFrom,
        setDateTo,
        setAppliedFilters,
      });
    }, [selectedItem?.id]);

    const handleApplyFilter = (nextFilters) => {
      setAppliedFilters(nextFilters);
    };

    const handleClearFilter = () => {
      resetFilterState({
        setFilterField,
        setFilterValue,
        setDateFrom,
        setDateTo,
        setAppliedFilters,
      });
    };

    const handlePrint = useReactToPrint({
      content: () => itemRef.current,
      documentTitle: "Item Ledger",
    });

    const handleDownload = () => {
      try {
        dispatch(triggerLoader());
        const input = itemRef.current;

        html2canvas(input, { scale: 1 })
          .then((canvas) => {
            const imgData = canvas.toDataURL("image/jpeg", 0.75);
            const pdf = new jsPDF();
            const imgProps = pdf.getImageProperties(imgData);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

            pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
            const name = selectedItem?.item_name ?? "ItemLedger";
            const date = Calculation.GetCurrentDateString();
            const pdfName = name + "_" + date + ".pdf";
            setTimeout(() => {
              pdf.save(pdfName);
              dispatch(triggerLoader());
            }, 5000);
          })
          .catch((e) => {
            console.log(e);
            dispatch(triggerLoader());
          });
      } catch (e) {
        console.log(e);
        dispatch(triggerLoader());
      }
    };

    const showLedgerContent =
      !itemLedgerLoading && itemLedger.length > 0;

    return (
      <>
        <Dialog
          open={openItemLedger}
          onClose={handleItemLedgerClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          fullWidth
          maxWidth={false}
          PaperProps={{
            sx: {
              width: "80%",
              height: "80%",
            },
          }}
        >
          <PopupDialogTitle
            handleDownload={handleDownload}
            handlePrint={handlePrint}
            handleClose={handleItemLedgerClose}
          />
          <DialogContent>
            {showLedgerContent && (
              <ItemLedgerFilter
                itemLedger={itemLedger}
                filteredCount={filteredItemLedger.length}
                filterField={filterField}
                setFilterField={setFilterField}
                filterValue={filterValue}
                setFilterValue={setFilterValue}
                dateFrom={dateFrom}
                setDateFrom={setDateFrom}
                dateTo={dateTo}
                setDateTo={setDateTo}
                onApply={handleApplyFilter}
                onClear={handleClearFilter}
              />
            )}

            {isMobile ? (
              <Box
                component={Paper}
                sx={{
                  padding: "16px",
                  backgroundColor: "#fff",
                  overflowY: "auto",
                  maxHeight: "80vh",
                  border: "none",
                }}
                ref={itemRef}
              >
                <ItemLedgerCard
                  itemLedger={filteredItemLedger}
                  selectedItem={selectedItem}
                  itemLedgerLoading={itemLedgerLoading}
                  totalCount={itemLedger.length}
                />
              </Box>
            ) : (
              <Box
                component={Paper}
                sx={{
                  padding: "16px",
                  backgroundColor: "#fff",
                  overflowY: "auto",
                  maxHeight: "80vh",
                  border: "none",
                }}
                ref={itemRef}
              >
                <ItemLedgerTable
                  itemLedger={filteredItemLedger}
                  selectedItem={selectedItem}
                  itemLedgerLoading={itemLedgerLoading}
                  totalCount={itemLedger.length}
                />
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  },
);

export default memo(ItemLedgerPopup);
